import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Card, CardActionArea, CardContent, Chip, Typography } from '@mui/material';
import { Project } from '../../interfaces/project';
import { ProjectService } from '../../services/project.service';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';
import { statusColor, statusLabel } from './ProjectDialogs';

/** P1-A: the projects the signed-in user manages (any member; no PROJECT_VIEW needed). */
const MyProjects: React.FC = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const [rows, setRows] = useState<Project[] | null>(null);

    useEffect(() => {
        ProjectService.mine().then(setRows).catch(err => { setRows([]); toast.showApiError(err, 'Failed to load your projects'); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>My projects</Typography>
            {rows === null && <Typography>Loading...</Typography>}
            {rows !== null && rows.length === 0 && (
                <Alert severity="info" data-testid="my-projects-empty">You do not manage any project yet.</Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {(rows ?? []).map(p => (
                    <Card key={p.id} variant="outlined" sx={{ flex: '1 1 280px', maxWidth: 420 }}>
                        <CardActionArea onClick={() => navigate(`/accounting/projects/${p.id}`)}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{p.code} {p.name}</Typography>
                                    <Chip size="small" label={statusLabel[p.status]} color={statusColor[p.status]} />
                                </Box>
                                {p.customerName && <Typography variant="body2" color="text.secondary">{p.customerName}</Typography>}
                                <Typography variant="body2">
                                    Cost {formatMoney(p.actualCost ?? 0)}
                                    {p.budgetCost != null ? ` of ${formatMoney(p.budgetCost)} (${p.costUsedPct ?? 0}%)` : ''}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};

export default MyProjects;
